import {
  IDataLayer,
  IGlobalMetadata,
  IPageLoadData,
  ITrackEventData,
  ITrackEventDealerData,
  ITrackEventOfferData,
  ITrackEventVideoData,
} from 'interfaces/analytics';
import { cleanHTMLFromString } from './FormatText';

/**
 * @description: This script handles the anlaytics logic used in the acura shopping tools
 **/

export function getBodyStyle(modelSeries: string) {
  if (/ilx|tlx/i.test(modelSeries)) {
    return 'sedan';
  }
  if (/mdx|rdx|zdx|adx/i.test(modelSeries)) {
    return 'suv';
  }
  if (/integra/i.test(modelSeries)) {
    return 'compact';
  }
  if (/nsx/i.test(modelSeries)) {
    return 'supercar';
  }
  return 'no model';
}

function createObjHtmlClean(key: string, value?: string) {
  return value ? { [key]: cleanHTMLFromString(value) } : {};
}

function createObjFromProp(key: string, value?: string) {
  return value ? { [key]: value } : {};
}

function getUserObj({
  drivetrain,
  powertrain,
  transmission,
  user,
}: ITrackEventData) {
  if (!drivetrain && !powertrain && !transmission && !user) {
    return {};
  }
  return {
    ...createObjHtmlClean('selected_drivetrain', drivetrain),
    ...createObjHtmlClean('selected_powertrain', powertrain),
    ...createObjHtmlClean('selected_transmission', transmission),
    ...createObjHtmlClean('bap_interior_accessories', user?.bap_accessories),
  };
}

function getDealerObj(dealer?: ITrackEventDealerData) {
  if (!dealer) {
    return {};
  }
  return {
    Dealer: {
      ...createObjFromProp('selected_dealer_id', dealer.dealerId),
      ...createObjFromProp('selected_dealer_name', dealer.dealerName),
      ...createObjFromProp('selected_dealer_zipcode', dealer.dealerZipcode),
      ...createObjFromProp('selected_dealer_address', dealer.dealerAddress),
      ...createObjFromProp('selected_dealer_phone_number', dealer.dealerPhone),
      ...createObjFromProp('selected_dealer_link', dealer.dealerLink),
    },
  };
}
function getVideoObj(video?: ITrackEventVideoData) {
  if (!video) {
    return {};
  }
  return {
    VideoMetadata: {
      ...createObjFromProp('video_title', video.videoTitle),
      ...createObjFromProp('video_action', video.videoAction),
      ...createObjFromProp('video_id', video.videoId),
      ...createObjFromProp('video_link', video.videoLink),
      ...createObjFromProp('video_milestone', video.videoMilestone),
      ...createObjFromProp('video_event_duration', video.videoEventDuration),
    },
  };
}

function getOfferObj(offer?: ITrackEventOfferData) {
  if (!offer) {
    return {};
  }
  return {
    Offer: {
      ...createObjFromProp('offer_amount', offer.offerAmount),
      ...createObjFromProp('offer_date', offer.offerDate),
      ...createObjFromProp('offer_duration', offer.offerDuration),
      ...createObjFromProp('offer_expire', offer.offerExpire),
      ...createObjFromProp('offer_id', offer.offerId),
      ...createObjFromProp('offer_model', offer.offerModel),
      ...createObjFromProp('offer_name', offer.offerName),
      ...createObjFromProp('offer_start', offer.offerStart),
      ...createObjFromProp('offer_term', offer.offerTerm),
      ...createObjFromProp('offer_type', offer.offerType),
      ...createObjFromProp('offer_year', offer.offerYear),
    },
  };
}

export function setGlobalMetadata(globalMetaData: IGlobalMetadata) {
  const Acr = window.Acr;
  const updateDataLayer = { ...Acr.dataLayer };
  updateDataLayer.GlobalMetadata = globalMetaData;
  Acr.dataLayer = updateDataLayer;
}

function setReferrerType(referrerUrl: string) {
  const Acr = window.Acr;
  var type = 'external';
  const socialMedia = Acr.referrerList?.socialNetworks;
  const searchEngine = Acr.referrerList?.searchEngines;
  if (!referrerUrl) {
    type = 'typed/bookmark';
  } else if (
    socialMedia &&
    !socialMedia.every(
      (element) => !referrerUrl.toLowerCase().includes(element)
    )
  ) {
    type = 'social_networks';
  } else if (
    searchEngine &&
    !searchEngine.every(
      (element) => !referrerUrl.toLowerCase().includes(element)
    )
  ) {
    type = 'search_engine';
  } else if (referrerUrl.includes('acura')) {
    type = 'internal';
  }
  return type;
}

export function pageLoad(pageLoadData: IPageLoadData) {
  const Acr = window.Acr;
  const digitalData = window.digitalData;
  // Analytics for autos.honda, just save data in digitalData
  if (digitalData) {
    const { page, model } = digitalData;
    const {
      modelDetails,
      modelSeries,
      modelYear,
      modelTrim,
      pageName,
      subSection,
      subSection2,
    } = pageLoadData;

    digitalData.model = {
      ...model,
      model_name: modelSeries,
      model_year: modelYear,
      model_trim: modelTrim,
      selected_exterior_color: modelDetails?.exteriorColor,
      selected_interior_color: modelDetails?.interiorColor,
    };

    digitalData.page = {
      ...page,
      page_name: pageName,
      sub_section: subSection,
      sub_section2: subSection2,
    };
  }
  if (window.Acr) {
    const {
      modelSeries,
      modelYear,
      modelId,
      modelTrim,
      pageName,
      subSection,
      subSection2,
      dealer,
      modelDetails,
      hideModelFromPageName,
      user,
      siteSection,
      interiorColor,
      exteriorColor,
    } = pageLoadData;

    const globalMetaData = Acr?.dataLayer?.GlobalMetadata || {};
    const baseDataLayer: IDataLayer = {
      Model: {
        model_brand: 'acura',
      },
      Page: {
        brand_name: 'Acura',
        full_url: window.location.href,
        page_friendly_url:
          document
            .querySelector("link[rel='canonical']")
            ?.getAttribute('href') || '',
        referrer_type: setReferrerType(document.referrer),
        internal_referrer: document.referrer.includes('acura')
          ? document.referrer
          : '',
        referrer_url: document.referrer.includes('acura')
          ? ''
          : document.referrer,
        site_display_format:
          window.innerWidth < 550
            ? 'mobile'
            : window.innerWidth < 1024
            ? 'tablet'
            : 'desktop',
        site_language_code:
          document.getElementsByTagName('html')[0].getAttribute('lang') || 'en',
        site_section: siteSection ? siteSection : 'shopping tools',
      },
      User: {
        cookie_consent_category: window.OnetrustActiveGroups || '',
        user_zipcode: localStorage.getItem('userZipCode') || undefined,
      },
      GlobalMetadata: globalMetaData,
    };

    if (modelSeries && modelYear && pageName && subSection) {
      const pageView: IDataLayer = { ...baseDataLayer };
      const useModelSeriesInPageName =
        modelSeries !== 'no model' && !hideModelFromPageName;
      pageView.Model = {
        model_brand: 'acura',
        model_name: modelSeries,
        model_year: modelYear,
        model_id: modelId,
        model_trim: modelTrim,
        body_style: getBodyStyle(modelSeries),
        selected_exterior_color: modelDetails?.exteriorColor || exteriorColor,
        selected_interior_color: modelDetails?.interiorColor || interiorColor,
      };
      pageView.GlobalMetadata = {
        property_name: 'acura.com',
      };
      pageView.Page.page_name = `${
        useModelSeriesInPageName ? `${modelSeries} ` : ''
      }${pageName}`;
      pageView.Page.sub_section = subSection;
      pageView.Page.sub_section2 = subSection2 || modelSeries;
      if (dealer) {
        pageView.Dealer = {
          selected_dealer_id: dealer.dealerId,
          selected_dealer_zipcode: dealer.dealerZipcode,
          selected_dealer_name: dealer.dealerName,
          selected_dealer_address: dealer.dealerAddress,
          selected_dealer_phone_number: dealer.dealerPhone,
        };
      }
      if (modelDetails) {
        const userData = Object.assign(pageView.User ? pageView.User : {}, {
          selected_exterior_color: modelDetails.exteriorColor,
          selected_interior_color: modelDetails.interiorColor,
          bap_interior_accessories: modelDetails.interiorAccessories,
          bap_exterior_accessories: modelDetails.exteriorAccessories,
          bap_total_as_configured: modelDetails.msrp,
          selected_powertrain: modelDetails.powertrain,
          selected_drivetrain: modelDetails.drivetrain,
        });
        pageView.User = userData;
      }
      if (user) {
        const newUser = Object.assign(pageView.User ? pageView.User : {}, {
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          phone_number: user.phone_number,
          user_zipcode: user.zip_code,
          marketing_optin: user.marketing_optin,
        });
        pageView.User = newUser;
      }
      Acr.pageView = pageView;
      Acr.dataLayer = pageView;

      let pageLoadAttemps = 0;

      if (window._satellite?.track && window.OnetrustActiveGroups) {
        window._satellite.track('page-load');
      } else {
        /*if the analytics script is not ready
          an interval is created to wait for 
          the analytics to be ready
        */
        const pageLoadInterval = setInterval(() => {
          if (
            window._satellite?.track &&
            (window.OnetrustActiveGroups || pageLoadAttemps >= 4)
          ) {
            Acr.dataLayer = pageView;
            if (Acr.pageView.User) {
              Acr.pageView.User.cookie_consent_category =
                window.OnetrustActiveGroups || '';
            }
            if (Acr.dataLayer.User) {
              Acr.dataLayer.User.cookie_consent_category =
                window.OnetrustActiveGroups || '';
            }
            window._satellite.track('page-load');
            clearInterval(pageLoadInterval);
          } else {
            pageLoadAttemps++;
          }
        }, 1000);
      }
    }
  }
}

export function trackEvent({
  dealer,
  video,
  offer,
  ...trackEventData
}: ITrackEventData) {
  const Acr = window.Acr || {};
  const { pageView, dataLayer } = Acr;
  const digitalData = window.digitalData;

  // Analytics for autos.honda just save data on digitalData
  if (digitalData) {
    const { page, model, event_metadata } = digitalData;
    const pageName = page.page_name || '';
    const includePageName = trackEventData.category.includePageName;
    const actionCategoryPageName = includePageName ? pageName : '';
    const actionCategoryText = cleanHTMLFromString(
      trackEventData.category.text
    );
    const actionLabelText = cleanHTMLFromString(trackEventData.label);
    const {
      selectedExteriorColor,
      selectedInteriorColor,
      listedMsrp,
      totalMsrp,
    } = trackEventData;

    digitalData.model = {
      ...model,
      selected_exterior_color: selectedExteriorColor,
      selected_interior_color: selectedInteriorColor,
      listed_msrp: listedMsrp,
      total_msrp: totalMsrp,
    };

    digitalData.event_metadata = {
      ...event_metadata,
      action_category: `${actionCategoryPageName} ${actionCategoryText}`.trim(),
      action_label: `${pageName}:${actionLabelText}`,
      action_type: trackEventData.type,
    };
  }

  if (pageView || dataLayer) {
    const modelObj = pageView?.Model || dataLayer?.Model || {};
    const pageObj = pageView?.Page || dataLayer?.Page;
    const modelName = trackEventData.modelSeries || modelObj.model_name || '';
    const pageName = pageObj.page_name || '';
    const includePageName = trackEventData.category.includePageName;
    const actionCategoryPageName = includePageName ? pageName : '';
    const actionCategoryText = cleanHTMLFromString(
      trackEventData.category.text
    );
    const actionLabelText = cleanHTMLFromString(trackEventData.label);
    const globalMetaData = dataLayer.GlobalMetadata || {};

    const trackEvent: IDataLayer = {
      EventMetadata: {
        action_category:
          `${actionCategoryPageName} ${actionCategoryText}`.trim(),
        action_label: `${pageName}:${actionLabelText}`,
        action_type: trackEventData.type,
        download_title: trackEventData.downloadTitle,
        events: trackEventData.events,
      },
      Model: {
        model_brand: 'Acura',
        model_name: modelName,
        model_year: trackEventData.modelYear || modelObj.model_year || '',
        body_style: getBodyStyle(modelName),
        model_trim:
          trackEventData.modelTrim !== undefined
            ? trackEventData.modelTrim
            : cleanHTMLFromString(modelObj.model_trim) || '',
        model_id: trackEventData.modelId || modelObj.model_id || '',
        selected_exterior_color: trackEventData.selectedExteriorColor,
        selected_interior_color: trackEventData.selectedInteriorColor,
        listed_msrp: trackEventData.listedMsrp,
        total_msrp: trackEventData.totalMsrp,
      },
      Page: {
        ...Acr?.pageView?.Page,
        page_name: pageName,
        site_language_code:
          document.getElementsByTagName('html')[0].getAttribute('lang') || 'en',
      },
      User: {
        cookie_consent_category: window.OnetrustActiveGroups || '',
        user_zipcode: trackEventData.zip_code || '',
        ...Acr?.pageView?.User,
        ...getUserObj(trackEventData),
      },
      ...getDealerObj(dealer),
      ...getVideoObj(video),
      ...getOfferObj(offer),
      GlobalMetadata: globalMetaData,
    };
    if (trackEventData.trackingEventName === 'compliance-click') {
      Acr.dataLayer.Page.content_publish_date =
        trackEventData.contentPublishDate;
    }

    Acr.dataLayer = trackEvent;

    window._satellite?.track &&
      window._satellite.track(
        trackEventData.trackingEventName || 'click-event'
      );
  }
}

export function trackInputDetailsEvent(label: string) {
  trackEvent({
    label: label,
    category: {
      text: 'details',
      includePageName: true,
    },
    type: 'input',
  });
}
